import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getDiscoverPostById,
  toggleDiscoverPostLike,
  getDiscoverPostComments,
  createDiscoverPostComment,
  shareDiscoverPost,
  addFavorite,
  removeFavorite,
  type DiscoverPostDetail,
  type DiscoverComment,
} from '@/lib/api';
import { useTk, useLocale } from '@/lib/i18n';
import { useAuth } from '@/lib/use-auth';
import { formatDateTime } from '@/lib/format';
import { trackBehavior } from '@/lib/track';
import { Button, Card, EmptyState, List, ListItem, Skeleton } from '@sabay/ui-kit';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function pickTitle(p: DiscoverPostDetail, locale: string): string {
  if (locale === 'km-KH' && p.titleKm) return p.titleKm;
  if (locale === 'en' && p.titleEn) return p.titleEn;
  return p.titleZh;
}

function pickContent(
  content: DiscoverPostDetail['contentI18n'],
  locale: string,
): string {
  if (locale === 'km-KH') return content.km ?? content.zh ?? content.en ?? '';
  if (locale === 'en') return content.en ?? content.zh ?? '';
  return content.zh ?? content.en ?? '';
}

/* ------------------------------------------------------------------ */
/*  Load state                                                        */
/* ------------------------------------------------------------------ */

type PageStatus = 'loading' | 'success' | 'error' | 'not-found';

interface PageState {
  status: PageStatus;
  post: DiscoverPostDetail | null;
  errorMessage: string | null;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export function DiscoverPostDetailPage(): React.ReactElement {
  const tk = useTk();
  const locale = useLocale();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const token = auth.token;
  const viewTrackedRef = useRef<string | null>(null);

  const [pageState, setPageState] = useState<PageState>({
    status: 'loading',
    post: null,
    errorMessage: null,
  });

  // Like state
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  // Favorite state
  const [favorited, setFavorited] = useState(false);
  const [favoriteId, setFavoriteId] = useState<string | null>(null);

  // Comment state
  const [comments, setComments] = useState<DiscoverComment[]>([]);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentDraft, setCommentDraft] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Share feedback
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);

  // Toast auto-dismiss
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showFeedback(msg: string): void {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    setShareFeedback(msg);
    feedbackTimer.current = setTimeout(() => setShareFeedback(null), 3000);
  }

  // Fetch post detail
  useEffect(() => {
    if (!id) return;
    const ac = new AbortController();
    setPageState({ status: 'loading', post: null, errorMessage: null });

    getDiscoverPostById(id, token, ac.signal)
      .then((res) => {
        setPageState({ status: 'success', post: res.post, errorMessage: null });
        setLiked(res.post.likedByMe);
        setLikeCount(res.post.likeCount);
      })
      .catch((err: Error) => {
        if (ac.signal.aborted) return;
        const is404 = (err as { status?: number }).status === 404;
        setPageState({
          status: is404 ? 'not-found' : 'error',
          post: null,
          errorMessage: err.message,
        });
      });

    return () => ac.abort();
  }, [id, token]);

  useEffect(() => {
    if (pageState.status !== 'success' || !pageState.post?.id) return;
    if (auth.status !== 'authenticated' || !auth.token) return;
    const postId = pageState.post.id;
    if (viewTrackedRef.current === postId) return;
    viewTrackedRef.current = postId;
    void trackBehavior('VIEW_DISCOVER_POST', { postId }, auth.token);
  }, [pageState.status, pageState.post?.id, auth.status, auth.token]);

  // Fetch comments
  useEffect(() => {
    if (!id) return;
    const ac = new AbortController();
    getDiscoverPostComments(id, { page: 1, pageSize: 50 }, ac.signal)
      .then((res) => {
        setComments(res.items);
        setCommentTotal(res.total);
      })
      .catch(() => {
        // Silently fail — comments are secondary content
      });
    return () => ac.abort();
  }, [id]);

  // --- Like toggle ---
  const handleLike = useCallback(async () => {
    if (!id || !token) return;
    try {
      const res = await toggleDiscoverPostLike(id, token);
      setLiked(res.liked);
      setLikeCount(res.likeCount);
    } catch {
      showFeedback(tk('common.error.generic'));
    }
  }, [id, token, tk]);

  // --- Favorite toggle ---
  const handleFavorite = useCallback(async () => {
    if (!id || !token) return;
    try {
      if (favorited && favoriteId) {
        await removeFavorite(favoriteId, token);
        setFavorited(false);
        setFavoriteId(null);
      } else {
        const res = await addFavorite('DISCOVER_POST', id, token);
        setFavorited(true);
        setFavoriteId(res.favorite.id);
      }
    } catch {
      showFeedback(tk('common.error.generic'));
    }
  }, [id, token, favorited, favoriteId, tk]);

  // --- Share ---
  const handleShare = useCallback(async () => {
    if (!id) return;

    // Record share on server (fire-and-forget if authenticated)
    if (token) {
      void shareDiscoverPost(id, token).catch(() => undefined);
    }

    // Try Telegram shareToStory, fallback to copy link
    const shareUrl = `${window.location.origin}/discover/${encodeURIComponent(id)}`;

    try {
      const tg = (window as unknown as { Telegram?: { WebApp?: { shareToStory?: (url: string) => void } } }).Telegram;
      if (tg?.WebApp?.shareToStory) {
        tg.WebApp.shareToStory(shareUrl);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        showFeedback(tk('discover.share.copied'));
      }
    } catch {
      showFeedback(tk('discover.share.copied'));
    }
  }, [id, token, tk]);

  // --- Submit comment ---
  const handleSubmitComment = useCallback(async () => {
    const trimmed = commentDraft.trim();
    if (!trimmed || !id || !token) return;
    setSubmittingComment(true);
    try {
      const res = await createDiscoverPostComment(id, trimmed, token);
      setComments((prev) => [res.comment, ...prev]);
      setCommentTotal((prev) => prev + 1);
      setCommentDraft('');
    } catch {
      showFeedback(tk('common.error.generic'));
    } finally {
      setSubmittingComment(false);
    }
  }, [commentDraft, id, token, tk]);

  /* ------------------------------------------------------------------ */
  /*  Render: loading / error / not-found                               */
  /* ------------------------------------------------------------------ */

  if (pageState.status === 'loading') {
    return (
      <main className="min-h-screen pb-24">
        <header className="sticky top-0 z-20 px-5 pt-5 pb-3 bg-clay-50/85 backdrop-blur-md">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="!h-8 min-w-0 justify-start px-0 font-normal text-clay-600"
          >
            ← {tk('common.cancel')}
          </Button>
        </header>
        <div className="mt-8 px-5">
          <Card variant="flat" padding="lg" className="border-dashed border-clay-200">
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="aspect-video w-full rounded-xl" />
              <Skeleton shape="text" lines={3} />
            </div>
          </Card>
        </div>
      </main>
    );
  }

  if (pageState.status === 'not-found') {
    return (
      <main className="min-h-screen pb-24">
        <header className="sticky top-0 z-20 px-5 pt-5 pb-3 bg-clay-50/85 backdrop-blur-md">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="!h-8 min-w-0 justify-start px-0 font-normal text-clay-600"
          >
            ← {tk('common.cancel')}
          </Button>
        </header>
        <div className="mt-8 px-5">
          <Card variant="flat" padding="none" className="border-clay-200">
            <EmptyState className="py-10" title={tk('discover.empty')} />
          </Card>
        </div>
      </main>
    );
  }

  if (pageState.status === 'error') {
    return (
      <main className="min-h-screen pb-24">
        <header className="sticky top-0 z-20 px-5 pt-5 pb-3 bg-clay-50/85 backdrop-blur-md">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="!h-8 min-w-0 justify-start px-0 font-normal text-clay-600"
          >
            ← {tk('common.cancel')}
          </Button>
        </header>
        <div className="mt-8 px-5">
          <Card variant="flat" padding="md" className="border-danger-500/40 bg-danger-500/10">
            <p className="text-sm font-medium text-danger-500">{tk('common.error.generic')}</p>
            <p className="mt-1 break-all text-xs text-danger-500 opacity-75">{pageState.errorMessage}</p>
          </Card>
        </div>
      </main>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Render: success                                                   */
  /* ------------------------------------------------------------------ */

  const post = pageState.post!;
  const title = pickTitle(post, locale);
  const content = pickContent(post.contentI18n, locale);

  return (
    <main className="min-h-screen pb-32">
      {/* Header */}
      <header className="sticky top-0 z-20 flex items-center justify-between bg-clay-50/85 px-5 pb-3 pt-5 backdrop-blur-md">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="!h-8 min-w-0 justify-start px-0 font-normal text-clay-600"
        >
          ← {tk('common.cancel')}
        </Button>
        <span className="eyebrow text-clay-400">{tk('discover.title')}</span>
        <div className="w-10" />
      </header>

      {/* Share feedback toast */}
      {shareFeedback ? (
        <div className="mx-5 mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {shareFeedback}
        </div>
      ) : null}

      {/* Cover image */}
      {post.coverImageUrl ? (
        <div
          className="w-full aspect-[16/9] bg-clay-100 bg-cover bg-center"
          style={{ backgroundImage: `url(${post.coverImageUrl})` }}
          role="img"
          aria-label={title}
        />
      ) : (
        <div
          className="w-full aspect-[16/9] grid place-items-center"
          style={{
            background:
              'linear-gradient(135deg, #FFE3BD 0%, #FFB050 60%, #E5741F 100%)',
          }}
          aria-hidden
        >
          <span className="font-serif text-5xl font-semibold text-clay-900/70">
            {(title || '?').slice(0, 1)}
          </span>
        </div>
      )}

      {/* Post content */}
      <div className="px-5 pt-5 pb-4 space-y-3">
        <h1 className="font-serif text-2xl font-semibold text-clay-900 leading-snug">
          {title}
        </h1>

        {/* Merchant card */}
        <Link
          to={`/merchants/${post.merchantId}`}
          className="flex items-center gap-3 rounded-2xl border border-clay-200 bg-white p-3 active:scale-[0.99] transition"
        >
          <div className="w-12 h-12 rounded-xl bg-amber-100 grid place-items-center text-amber-700 font-semibold text-sm shrink-0">
            {(post.merchantNameZh || '?').slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-clay-900 truncate">
              {post.merchantNameZh}
            </p>
            <p className="text-xs text-clay-500">{tk('merchant.section.deals')}</p>
          </div>
          <svg
            className="w-4 h-4 text-clay-400 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </Link>

        {/* Body text */}
        <div className="text-sm text-clay-700 leading-relaxed whitespace-pre-line">
          {content}
        </div>

        {/* Published date */}
        {post.publishedAt ? (
          <p className="text-xs text-clay-400">
            {formatDateTime(post.publishedAt, locale)}
          </p>
        ) : null}
      </div>

      {/* Divider */}
      <div className="mx-5 border-t border-clay-100" />

      {/* Comments section */}
      <div className="px-5 pt-4 pb-4 space-y-3">
        <h2 className="text-sm font-semibold text-clay-800">
          {tk('discover.actions.comment')} ({commentTotal})
        </h2>

        {/* Comment input */}
        {token ? (
          <div className="flex gap-2">
            <textarea
              aria-label={tk('discover.comment.placeholder')}
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.currentTarget.value)}
              rows={1}
              className="flex-1 rounded-2xl border border-clay-200 bg-white px-3 py-2 text-sm text-clay-800 placeholder:text-clay-400 resize-none"
              placeholder={tk('discover.comment.placeholder')}
            />
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={submittingComment}
              disabled={!commentDraft.trim() || submittingComment}
              onClick={handleSubmitComment}
              className="shrink-0 !rounded-full"
            >
              {tk('discover.comment.submit')}
            </Button>
          </div>
        ) : null}

        {/* Comment list */}
        {comments.length === 0 ? (
          <EmptyState className="py-6" title={tk('merchant.reviews.empty')} />
        ) : (
          <List className="divide-y-0 gap-2">
            {comments.map((c) => (
              <ListItem key={c.id} className="rounded-2xl border-0 bg-clay-50 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="flex-1 text-sm text-clay-700">{c.content}</p>
                  <span className="mt-0.5 shrink-0 text-[10px] text-clay-400">
                    {formatDateTime(c.createdAt, locale)}
                  </span>
                </div>
              </ListItem>
            ))}
          </List>
        )}
      </div>

      {/* Bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/90 backdrop-blur-md border-t border-clay-100 px-5 py-3">
        <div className="grid grid-cols-4 gap-2 text-xs font-medium text-clay-600">
          {/* Like */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleLike}
            disabled={!token}
            className="!h-auto flex-col gap-0.5 !rounded-full border-0 bg-clay-50 py-2 font-medium text-clay-600 shadow-none hover:bg-clay-100"
          >
            <svg
              className={`h-5 w-5 ${liked ? 'fill-red-500 text-red-500' : 'text-clay-500'}`}
              fill={liked ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={liked ? 0 : 2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <span>{likeCount > 0 ? likeCount : tk('discover.actions.like')}</span>
          </Button>

          {/* Comment */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              document
                .querySelector<HTMLTextAreaElement>('textarea[aria-label]')
                ?.focus();
            }}
            className="!h-auto flex-col gap-0.5 !rounded-full border-0 bg-clay-50 py-2 font-medium text-clay-600 shadow-none hover:bg-clay-100"
          >
            <svg
              className="h-5 w-5 text-clay-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <span>{commentTotal > 0 ? commentTotal : tk('discover.actions.comment')}</span>
          </Button>

          {/* Favorite */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleFavorite}
            disabled={!token}
            className="!h-auto flex-col gap-0.5 !rounded-full border-0 bg-clay-50 py-2 font-medium text-clay-600 shadow-none hover:bg-clay-100"
          >
            <svg
              className={`h-5 w-5 ${favorited ? 'fill-amber-500 text-amber-500' : 'text-clay-500'}`}
              fill={favorited ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={favorited ? 0 : 2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
              />
            </svg>
            <span>
              {favorited ? tk('discover.actions.favorited') : tk('discover.actions.favorite')}
            </span>
          </Button>

          {/* Share */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleShare}
            className="!h-auto flex-col gap-0.5 !rounded-full border-0 bg-clay-50 py-2 font-medium text-clay-600 shadow-none hover:bg-clay-100"
          >
            <svg
              className="h-5 w-5 text-clay-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
            <span>{tk('discover.actions.share')}</span>
          </Button>
        </div>
      </div>
    </main>
  );
}
